import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

// Color themes for headers, sidebar buttons, and UI elements
export const colorThemes = {
  'sunset-teal': {
    name: 'Sunset Teal',
    headerGradient: 'from-[#C87B84] via-[#8B4A5E] to-[#007F85]',
    glowColor: 'rgba(255, 255, 255, 0.6)',
    // Accent colors for borders, outlines, highlights
    accentColor: '#C87B84',
    accentColorSecondary: '#007F85',
    // Progress bar gradient - single darker color
    progressGradient: 'from-[#8B4A5E] to-[#6B3A4E]',
    progressColor: '#8B4A5E',
    // Border/outline colors
    borderAccent: 'border-[#C87B84]/30',
    borderAccentHover: 'hover:border-[#C87B84]/60',
    // Ring/focus colors
    ringColor: 'ring-[#C87B84]/50',
    // Badge colors
    badgeGradient: 'from-[#C87B84] to-[#8B4A5E]',
    buttonGradients: {
      dashboard: 'from-[#3B82F6] to-[#2563EB]',
      projects: 'from-[#C87B84] to-[#8B4A5E]',
      myTasks: 'from-[#007F85] to-[#006066]',
      timeline: 'from-[#510C1A] to-[#3D0813]',
      companies: 'from-[#06B6D4] to-[#0891B2]',
      tags: 'from-[#F59E0B] to-[#D97706]',
      team: 'from-[#10B981] to-[#059669]',
    },
    preview: ['#C87B84', '#8B4A5E', '#007F85']
  },
  'ocean-mist': {
    name: 'Ocean Mist',
    headerGradient: 'from-[#0EA5E9] via-[#06B6D4] to-[#14B8A6]',
    glowColor: 'rgba(255, 255, 255, 0.6)',
    accentColor: '#0EA5E9',
    accentColorSecondary: '#14B8A6',
    // Progress bar gradient - single darker color
    progressGradient: 'from-[#0284C7] to-[#0369A1]',
    progressColor: '#0284C7',
    borderAccent: 'border-[#0EA5E9]/30',
    borderAccentHover: 'hover:border-[#0EA5E9]/60',
    ringColor: 'ring-[#0EA5E9]/50',
    badgeGradient: 'from-[#0EA5E9] to-[#06B6D4]',
    buttonGradients: {
      dashboard: 'from-[#0EA5E9] to-[#0284C7]',
      projects: 'from-[#06B6D4] to-[#0891B2]',
      myTasks: 'from-[#14B8A6] to-[#0D9488]',
      timeline: 'from-[#3B82F6] to-[#2563EB]',
      companies: 'from-[#8B5CF6] to-[#7C3AED]',
      tags: 'from-[#F59E0B] to-[#D97706]',
      team: 'from-[#10B981] to-[#059669]',
    },
    preview: ['#0EA5E9', '#06B6D4', '#14B8A6']
  },
  'purple-haze': {
    name: 'Purple Haze',
    headerGradient: 'from-[#8B5CF6] via-[#A855F7] to-[#EC4899]',
    glowColor: 'rgba(255, 255, 255, 0.6)',
    accentColor: '#8B5CF6',
    accentColorSecondary: '#EC4899',
    // Progress bar gradient - single darker color
    progressGradient: 'from-[#7C3AED] to-[#6D28D9]',
    progressColor: '#7C3AED',
    borderAccent: 'border-[#8B5CF6]/30',
    borderAccentHover: 'hover:border-[#8B5CF6]/60',
    ringColor: 'ring-[#8B5CF6]/50',
    badgeGradient: 'from-[#8B5CF6] to-[#A855F7]',
    buttonGradients: {
      dashboard: 'from-[#8B5CF6] to-[#7C3AED]',
      projects: 'from-[#A855F7] to-[#9333EA]',
      myTasks: 'from-[#EC4899] to-[#DB2777]',
      timeline: 'from-[#6366F1] to-[#4F46E5]',
      companies: 'from-[#06B6D4] to-[#0891B2]',
      tags: 'from-[#F59E0B] to-[#D97706]',
      team: 'from-[#10B981] to-[#059669]',
    },
    preview: ['#8B5CF6', '#A855F7', '#EC4899']
  },
  'rose-indigo': {
    name: 'Rose Indigo',
    headerGradient: 'from-[#E3BDCB] via-[#B4A3C8] to-[#4B4294]',
    glowColor: 'rgba(227, 189, 203, 0.5)',
    accentColor: '#E3BDCB',
    accentColorSecondary: '#4B4294',
    // Progress bar gradient - single darker color
    progressGradient: 'from-[#4B4294] to-[#3A3278]',
    progressColor: '#4B4294',
    borderAccent: 'border-[#B4A3C8]/30',
    borderAccentHover: 'hover:border-[#B4A3C8]/60',
    ringColor: 'ring-[#B4A3C8]/50',
    badgeGradient: 'from-[#E3BDCB] to-[#B4A3C8]',
    buttonGradients: {
      dashboard: 'from-[#B4A3C8] to-[#9A8DB8]',
      projects: 'from-[#4B4294] to-[#3A3278]',
      myTasks: 'from-[#E3BDCB] to-[#D4AEBB]',
      timeline: 'from-[#6366F1] to-[#4F46E5]',
      companies: 'from-[#06B6D4] to-[#0891B2]',
      tags: 'from-[#F59E0B] to-[#D97706]',
      team: 'from-[#10B981] to-[#059669]',
    },
    preview: ['#E3BDCB', '#B4A3C8', '#4B4294']
  },
  'midnight-dark': {
    name: 'Midnight Dark',
    headerGradient: 'from-[#1F2937] via-[#111827] to-[#030712]',
    glowColor: 'rgba(75, 85, 99, 0.3)',
    accentColor: '#6B7280',
    accentColorSecondary: '#374151',
    // Progress bar gradient - single darker gray
    progressGradient: 'from-[#4B5563] to-[#374151]',
    progressColor: '#4B5563',
    borderAccent: 'border-[#4B5563]/30',
    borderAccentHover: 'hover:border-[#4B5563]/60',
    ringColor: 'ring-[#4B5563]/50',
    badgeGradient: 'from-[#4B5563] to-[#374151]',
    buttonGradients: {
      dashboard: 'from-[#4B5563] to-[#374151]',
      projects: 'from-[#374151] to-[#1F2937]',
      myTasks: 'from-[#6B7280] to-[#4B5563]',
      timeline: 'from-[#374151] to-[#1F2937]',
      companies: 'from-[#4B5563] to-[#374151]',
      tags: 'from-[#6B7280] to-[#4B5563]',
      team: 'from-[#374151] to-[#1F2937]',
    },
    preview: ['#1F2937', '#111827', '#030712']
  },
  'ember-gold': {
    name: 'Ember Gold',
    headerGradient: 'from-[#F59E0B] via-[#D97706] to-[#92400E]',
    glowColor: 'rgba(245, 158, 11, 0.4)',
    accentColor: '#F59E0B',
    accentColorSecondary: '#92400E',
    // Progress bar gradient - single darker amber
    progressGradient: 'from-[#B45309] to-[#92400E]',
    progressColor: '#B45309',
    borderAccent: 'border-[#F59E0B]/30',
    borderAccentHover: 'hover:border-[#F59E0B]/60',
    ringColor: 'ring-[#F59E0B]/50',
    badgeGradient: 'from-[#F59E0B] to-[#D97706]',
    buttonGradients: {
      dashboard: 'from-[#F59E0B] to-[#D97706]',
      projects: 'from-[#D97706] to-[#B45309]',
      myTasks: 'from-[#92400E] to-[#78350F]',
      timeline: 'from-[#DC2626] to-[#B91C1C]',
      companies: 'from-[#EA580C] to-[#C2410C]',
      tags: 'from-[#CA8A04] to-[#A16207]',
      team: 'from-[#65A30D] to-[#4D7C0F]',
    },
    preview: ['#F59E0B', '#D97706', '#92400E']
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });

  const [colorTheme, setColorTheme] = useState(() => {
    const savedColorTheme = localStorage.getItem('colorTheme');
    return savedColorTheme || 'sunset-teal';
  });

  useEffect(() => {
    const root = document.documentElement;
    
    // Apply dark/light mode
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Persist preferences
    localStorage.setItem('theme', theme);
    localStorage.setItem('colorTheme', colorTheme);
  }, [theme, colorTheme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const changeColorTheme = (themeName) => {
    if (colorThemes[themeName]) {
      setColorTheme(themeName);
    }
  };

  // Get the current color theme configuration
  const currentColorTheme = colorThemes[colorTheme] || colorThemes['sunset-teal'];

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      toggleTheme, 
      colorTheme, 
      changeColorTheme,
      colorThemes,
      currentColorTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
